// Shared visual tokens for the light operations workspace.
export const tokensDark = {
  grey: {
    0: "#ffffff", // manually adjusted
    10: "#f6f6f6", // manually adjusted
    50: "#f0f0f0", // manually adjusted
    100: "#e0e0e0",
    200: "#c2c2c2",
    300: "#a3a3a3",
    400: "#858585",
    500: "#666666",
    600: "#525252",
    700: "#3d3d3d",
    800: "#292929",
    900: "#141414",
    1000: "#000000", // manually adjusted
  },
  primary: {
    100: "#ffe1e1",
    200: "#ffc3c4",
    300: "#ff9d9e",
    400: "#f87679",
    500: "#ed595f",
    600: "#d94148",
    700: "#b82f38",
    800: "#8e242c",
    900: "#611920",
  },
  secondary: {
    50: "#fff1f1",
    100: "#ffd9d9",
    200: "#ffb8b8",
    300: "#fb8b8b",
    400: "#f06464",
    500: "#e5484d",
    600: "#c9363d",
    700: "#a82932",
    800: "#7b2028",
    900: "#50151b",
  },
};

// Curated semantic tokens for the light operations workspace.
// In light mode, tokens 100-300 provide high-contrast text and labels against light backgrounds,
// avoiding washed-out pale tones.
export const tokensLight = {
  grey: tokensDark.grey,
  primary: tokensDark.primary,
  secondary: {
    50: "#fff1f1",
    100: "#2d0708", // High-contrast dark maroon for headers & primary labels
    200: "#5a0d10", // Deep maroon for values, stats, and prominent numbers
    300: "#891418", // Deep red for subtitles & secondary labels
    400: "#b71a20", // Accent red
    500: "#e5484d", // Prima primary brand red
    600: "#c9363d", // Vibrant action red
    700: "#a82932", // Darker action hover
    800: "#ffd9d9", // Subtle light tint for tags/chips
    900: "#fff1f1", // Lightest tint
  },
};

// mui theme settings
export const themeSettings = (mode) => {
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            // palette values for dark mode
            primary: {
              ...tokensDark.primary,
              main: tokensDark.primary[300],
              light: tokensDark.primary[400],
              dark: tokensDark.primary[700],
            },
            secondary: {
              ...tokensDark.secondary,
              main: tokensDark.secondary[300],
              light: tokensDark.secondary[200],
              dark: tokensDark.secondary[500],
            },
            neutral: {
              ...tokensDark.grey,
              main: tokensDark.grey[500],
            },
            background: {
              default: tokensDark.primary[800],
              alt: tokensDark.primary[700],
            },
          }
        : {
            // palette values for light mode
            primary: {
              ...tokensDark.primary,
              main: tokensDark.grey[50], // #f0f0f0 (neutral surface for unselected tabs)
              light: tokensDark.grey[0], // #ffffff (neutral light table body/cards)
              dark: tokensDark.primary[700], // #b82f38
            },
            secondary: {
              ...tokensLight.secondary,
              main: tokensDark.secondary[500], // #e5484d (Prima brand red)
              light: tokensDark.secondary[600], // #c9363d (rich action button red)
              dark: tokensDark.secondary[700], // #a82932 (button hover state)
            },
            neutral: {
              ...tokensDark.grey,
              main: tokensDark.grey[500],
            },
            background: {
              default: "#f5f7fb",
              alt: "#ffffff",
            },
          }),
    },
    typography: {
      fontFamily: ["Plus Jakarta Sans", "Inter", "sans-serif"].join(","),
      fontSize: 13,
      h1: {
        fontFamily: ["Plus Jakarta Sans", "Inter", "sans-serif"].join(","),
        fontSize: 40,
        fontWeight: 800,
      },
      h2: {
        fontFamily: ["Plus Jakarta Sans", "Inter", "sans-serif"].join(","),
        fontSize: 32,
        fontWeight: 800,
      },
      h3: {
        fontFamily: ["Plus Jakarta Sans", "Inter", "sans-serif"].join(","),
        fontSize: 24,
        fontWeight: 800,
      },
      h4: {
        fontFamily: ["Plus Jakarta Sans", "Inter", "sans-serif"].join(","),
        fontSize: 20,
        fontWeight: 800,
      },
      h5: {
        fontFamily: ["Plus Jakarta Sans", "Inter", "sans-serif"].join(","),
        fontSize: 16,
        fontWeight: 700,
      },
      h6: {
        fontFamily: ["Plus Jakarta Sans", "Inter", "sans-serif"].join(","),
        fontSize: 14,
        fontWeight: 700,
      },
    },
    shape: { borderRadius: 14 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 10, textTransform: "none", fontWeight: 700 },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 10 },
        },
      },
    },
  };
};
